import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, ExternalLink } from 'lucide-react';
import { OrgPgcRegistrationRow, OrgPgcRegistrationsApi } from '@/services/org-pgc-registrations.api';
import { PgcStatus } from '@/services/pgc-registrations.api';
import { UploadApi } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select, Textarea } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';
import { usePermissions } from '@/hooks/usePermissions';

function errMsg(e: unknown): string {
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong. Please try again.';
}

function statusTone(status: PgcStatus): 'amber' | 'green' {
  return status === 'completed' ? 'green' : 'amber';
}

// ───── Detail / status-progression modal ─────────────────────────────────
function RegistrationDetail({ registration, canWrite }: { registration: OrgPgcRegistrationRow; canWrite: boolean }) {
  const qc = useQueryClient();
  const [notes, setNotes] = useState(registration.counsellorNotes ?? '');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | undefined>();

  const invalidate = () => qc.invalidateQueries({ queryKey: ['org-pgc-registrations'] });

  const updateStatus = useMutation({
    mutationFn: (status: PgcStatus) => OrgPgcRegistrationsApi.updateStatus(registration.id, registration.schoolId, { status, counsellorNotes: notes }),
    onSuccess: invalidate,
  });

  const saveNotes = useMutation({
    mutationFn: () => OrgPgcRegistrationsApi.updateStatus(registration.id, registration.schoolId, { status: registration.status, counsellorNotes: notes }),
    onSuccess: invalidate,
  });

  const attachReport = useMutation({
    mutationFn: (reportUrl: string) => OrgPgcRegistrationsApi.attachReport(registration.id, registration.schoolId, reportUrl),
    onSuccess: invalidate,
  });

  async function handleReportPick(file: File) {
    setUploading(true);
    setUploadError(undefined);
    try {
      const res = await UploadApi.upload(file);
      attachReport.mutate(res.url);
    } catch (e) {
      setUploadError(errMsg(e));
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <Badge tone="indigo">{registration.schoolName}</Badge>
        <Badge tone="blue">{registration.studentName ?? 'Unknown student'}</Badge>
        <Badge tone="slate">{registration.parentName ?? 'Unknown parent'}</Badge>
        {registration.parentMobile && <span>{registration.parentMobile}</span>}
        <Badge tone={statusTone(registration.status)}>{registration.status}</Badge>
      </div>

      {registration.reason && (
        <div>
          <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Parent's Concern</h4>
          <p className="text-sm text-slate-700">{registration.reason}</p>
        </div>
      )}

      <div>
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Status</h4>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={!canWrite || registration.status === 'pending' || updateStatus.isPending}
            className="btn-secondary !py-1 text-xs disabled:opacity-50"
            onClick={() => updateStatus.mutate('pending')}
          >
            Mark Pending
          </button>
          <button
            type="button"
            disabled={!canWrite || registration.status === 'completed' || updateStatus.isPending}
            className="btn-primary !py-1 text-xs disabled:opacity-50"
            onClick={() => updateStatus.mutate('completed')}
          >
            Mark Completed
          </button>
        </div>
        {updateStatus.error && <p className="mt-1 text-xs text-red-600">{errMsg(updateStatus.error)}</p>}
      </div>

      <div>
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Counsellor Notes</h4>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} disabled={!canWrite} />
        {canWrite && (
          <button
            type="button"
            className="btn-secondary mt-2 !py-1 text-xs"
            disabled={saveNotes.isPending || notes === (registration.counsellorNotes ?? '')}
            onClick={() => saveNotes.mutate()}
          >
            {saveNotes.isPending ? 'Saving…' : 'Save Notes'}
          </button>
        )}
        {saveNotes.error && <p className="mt-1 text-xs text-red-600">{errMsg(saveNotes.error)}</p>}
      </div>

      <div>
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Counsellor Report</h4>
        {registration.counsellorReportUrl ? (
          <a href={registration.counsellorReportUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-brand-600 hover:underline">
            <FileText className="h-4 w-4 shrink-0" /> View report <ExternalLink className="h-3 w-3 shrink-0 text-slate-400" />
          </a>
        ) : (
          <p className="mb-2 text-xs text-slate-400">No report attached yet.</p>
        )}
        {canWrite && (
          <label className="btn-secondary mt-2 inline-block cursor-pointer !py-1 text-xs">
            {uploading ? 'Uploading…' : registration.counsellorReportUrl ? 'Replace report' : 'Attach report'}
            <input
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              className="hidden"
              disabled={uploading}
              onChange={(e) => e.target.files?.[0] && handleReportPick(e.target.files[0])}
            />
          </label>
        )}
        {uploadError && <p className="mt-1 text-xs text-red-600">{uploadError}</p>}
        {attachReport.error && <p className="mt-1 text-xs text-red-600">{errMsg(attachReport.error)}</p>}
      </div>

      <div>
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Status History</h4>
        {registration.statusHistory.length === 0 ? (
          <p className="text-xs text-slate-400">No history yet.</p>
        ) : (
          <ul className="space-y-2 border-l border-slate-200 pl-3">
            {registration.statusHistory.map((h, i) => (
              <li key={i} className="text-xs text-slate-600">
                <span className="font-medium text-slate-800">{h.status}</span> by {h.updated_by_name} — {h.time}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ───── Page ────────────────────────────────────────────────────────────────
export function OrgPgcRegistrationsPage() {
  const { can } = usePermissions();
  const canWrite = can('/pgc-registrations', 'create');

  const [schoolFilter, setSchoolFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<PgcStatus | ''>('');
  const [viewingId, setViewingId] = useState<string | null>(null);

  const { data: schools = [] } = useQuery({
    queryKey: ['org-pgc-schools'],
    queryFn: () => OrgPgcRegistrationsApi.schools(),
  });

  const { data = [], isFetching, error } = useQuery({
    queryKey: ['org-pgc-registrations', schoolFilter, statusFilter],
    queryFn: () => OrgPgcRegistrationsApi.list({ schoolId: schoolFilter || undefined, status: statusFilter || undefined }),
  });

  const viewing = data.find((r) => r.id === viewingId) ?? null;

  return (
    <div>
      <PageHeader
        title="PGC Registrations"
        description="Parent Guidance Council — parent-raised counselling requests across your organization's schools. Progress status, add counsellor notes, and attach a report."
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Select className="w-56" value={schoolFilter} onChange={(e) => setSchoolFilter(e.target.value)}>
          <option value="">All schools</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Select className="w-48" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as PgcStatus | '')}>
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="completed">Completed</option>
        </Select>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{errMsg(error)}</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-slate-600">School</th>
                <th className="px-4 py-2 text-left font-medium text-slate-600">Student</th>
                <th className="px-4 py-2 text-left font-medium text-slate-600">Parent</th>
                <th className="px-4 py-2 text-left font-medium text-slate-600">Concern</th>
                <th className="px-4 py-2 text-left font-medium text-slate-600">Status</th>
                <th className="px-4 py-2 text-left font-medium text-slate-600">Raised</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.map((r) => (
                <tr key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setViewingId(r.id)}>
                  <td className="px-4 py-2 text-slate-600">{r.schoolName}</td>
                  <td className="px-4 py-2 font-medium text-brand-600">{r.studentName ?? '—'}</td>
                  <td className="px-4 py-2 text-slate-700">{r.parentName ?? '—'}</td>
                  <td className="max-w-xs truncate px-4 py-2 text-slate-600">{r.reason ?? '—'}</td>
                  <td className="px-4 py-2">
                    <Badge tone={statusTone(r.status)}>{r.status}</Badge>
                  </td>
                  <td className="px-4 py-2 text-slate-500">{r.createdAt?.slice(0, 10)}</td>
                </tr>
              ))}
              {!isFetching && data.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-400">
                    No PGC registrations {statusFilter ? `with status "${statusFilter}"` : 'yet'}.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={!!viewing} onClose={() => setViewingId(null)} title="PGC Registration" size="lg">
        {viewing && <RegistrationDetail registration={viewing} canWrite={canWrite} />}
      </Modal>
    </div>
  );
}
